/**
 * ThreadView — port of NegotiationThreadPage body into a composable component.
 *
 * Renders the full thread detail including:
 * - Header with vendor name, status badge, mode toggle, Mark declined, View vendor
 * - Finalize deal panel (collapsible)
 * - Message list (MessageBubble for sent/received)
 * - Activity / audit trail (sender, send_kind, AI-draft marker, delivery status)
 * - VendorNotesPanel
 * - Pinned DraftCard for pending drafts
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  ChevronDown,
  Loader2,
  CheckCircle2,
  X,
  Bot,
  Sparkles,
  User,
  Mail,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { useNegotiationThread } from "@/hooks/useNegotiationThread";
import { MessageBubble } from "@/components/negotiations/MessageBubble";
import { DraftCard } from "@/components/negotiations/DraftCard";
import { VendorNotesPanel } from "@/components/negotiations/VendorNotesPanel";
import type { NegotiationThreadStatus, NegotiationMode } from "@/types/negotiations";
import type { NegotiationCase } from "@/lib/par/negotiationCase";

// ---------------------------------------------------------------------------
// Status styles (semantic tokens)
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<NegotiationThreadStatus, string> = {
  active: "bg-primary/15 text-primary border-primary/30",
  needs_attention: "bg-warning/15 text-warning border-warning/30",
  agreed: "bg-success/15 text-success border-success/30",
  declined: "bg-destructive/15 text-destructive border-destructive/30",
  abandoned: "bg-muted/50 text-muted-foreground border-border",
};

const STATUS_LABELS: Record<NegotiationThreadStatus, string> = {
  active: "Active",
  needs_attention: "Needs Attention",
  agreed: "Agreed",
  declined: "Declined",
  abandoned: "Abandoned",
};

// ---------------------------------------------------------------------------
// Delivery status badge
// ---------------------------------------------------------------------------

function DeliveryBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const styles: Record<string, string> = {
    delivered: "bg-success/10 text-success border-success/30",
    sent: "bg-primary/10 text-primary border-primary/30",
    bounced: "bg-destructive/10 text-destructive border-destructive/30",
    complained: "bg-warning/10 text-warning border-warning/30",
  };
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] px-1 py-0 border",
        styles[status] ?? "border-border/60 text-muted-foreground"
      )}
    >
      {status}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Activity trail entry
// ---------------------------------------------------------------------------

function ActivityEntry({
  label,
  detail,
  icon: Icon,
  time,
  isAuto,
}: {
  label: string;
  detail?: string;
  icon: React.ElementType;
  time?: string | null;
  isAuto?: boolean;
}) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-muted/40 flex items-center justify-center">
        <Icon className="w-3 h-3 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="font-medium text-foreground">{label}</span>
        {isAuto && (
          <Badge
            variant="outline"
            className="text-[10px] px-1 py-0 border-border/60 text-muted-foreground ml-1"
          >
            auto
          </Badge>
        )}
        {detail && <span className="text-muted-foreground ml-1">{detail}</span>}
      </div>
      {time && <span className="text-muted-foreground/60 flex-shrink-0">{time}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ThreadViewProps {
  threadId: string;
  /** Called when user clicks "← Back" or a parent wants to close the view. */
  onBack?: () => void;
  /** Called when user clicks "View vendor" — passes the vendor_name. */
  onViewVendor?: (vendorName: string) => void;
  /**
   * Data-backed context for the thread's vendor (built by buildNegotiationCase).
   * When provided, renders a collapsible "Context" section summarising why this
   * vendor was targeted — giving the operator background for the conversation.
   */
  vendorContext?: NegotiationCase | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThreadView({ threadId, onBack, onViewVendor, vendorContext }: ThreadViewProps) {
  const {
    thread,
    messages,
    isLoading,
    isGeneratingInitialDraft,
    approveSend,
    discardDraft,
    regenerateDraft,
    generateDraft,
    editDraft,
    finalize,
    setModeOverride,
    markDeclined,
    isSending,
    isDiscarding,
    isRegenerating,
    isGeneratingDraft,
    isFinalizing,
    isSettingModeOverride,
    isMarkingDeclined,
  } = useNegotiationThread(threadId);

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [agreedApr, setAgreedApr] = useState("");
  const [agreedDays, setAgreedDays] = useState("");
  const [activityOpen, setActivityOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Track whether this is the initial mount so we don't auto-scroll (and jump
  // the page viewport) when the thread is first opened. Only scroll into view
  // when new messages actually arrive after the component is already mounted.
  const prevMessageCountRef = useRef<number | null>(null);

  // Stable scroll helper — only called when message count genuinely increases
  // after first render, ensuring we never trigger a viewport jump on mount.
  const scrollToBottom = useCallback(() => {
    // scrollIntoView on a ref inside an overflow:auto container will scroll
    // only that container when block:"nearest" is used, avoiding a full-page
    // jump. "smooth" behaviour is preserved for new-message feedback.
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  useEffect(() => {
    if (prevMessageCountRef.current === null) {
      // First render: record the baseline count but do NOT scroll.
      prevMessageCountRef.current = messages.length;
      return;
    }
    if (messages.length > prevMessageCountRef.current) {
      // A new message arrived after initial mount — scroll to bottom.
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  const sentMessages = messages.filter((m) => m.status === "sent" || m.status === "received");
  const draftMessage = messages.find(
    (m) => m.status === "draft" || m.status === "pending_approval"
  );

  const handleFinalize = async () => {
    if (!threadId || !agreedApr || !agreedDays) return;
    await finalize(threadId, {
      agreed_apr: Number(agreedApr),
      agreed_days_early: Number(agreedDays),
    });
    setFinalizeOpen(false);
  };

  const handleToggleMode = async () => {
    if (!thread) return;
    const currentMode: NegotiationMode = thread.mode_override ?? "draft_wait";
    const nextMode: NegotiationMode = currentMode === "auto" ? "draft_wait" : "auto";
    await setModeOverride(threadId, nextMode);
  };

  const handleMarkDeclined = async () => {
    if (!window.confirm("Mark this negotiation as declined?")) return;
    await markDeclined(threadId);
  };

  function fmtTime(iso: string | null | undefined): string {
    if (!iso) return "";
    try {
      return format(parseISO(iso), "MMM d, h:mm a");
    } catch {
      return "";
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-sm text-muted-foreground">Thread not found.</p>
        {onBack && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            aria-label="Close thread"
            title="Close thread"
          >
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        )}
      </div>
    );
  }

  const currentMode: NegotiationMode = thread.mode_override ?? "draft_wait";
  const vendorDisplayName = thread.vendor_name ?? thread.supplier_name ?? thread.supplier_email;

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="border-b border-border bg-card px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-foreground truncate">
                {vendorDisplayName}
              </h3>
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] px-1.5 py-0 border flex-shrink-0",
                  STATUS_STYLES[thread.status]
                )}
              >
                {STATUS_LABELS[thread.status]}
              </Badge>
              {thread.status === "needs_attention" && (
                <AlertCircle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{thread.subject}</p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Mode toggle */}
            {(thread.status === "active" || thread.status === "needs_attention") && (
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-7 text-[11px] gap-1 px-2",
                  currentMode === "auto"
                    ? "border-success/40 text-success hover:bg-success/10"
                    : "text-muted-foreground"
                )}
                onClick={handleToggleMode}
                disabled={isSettingModeOverride}
                title={`Current mode: ${currentMode}. Click to toggle.`}
              >
                {isSettingModeOverride ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : currentMode === "auto" ? (
                  <Bot className="w-3 h-3" />
                ) : (
                  <User className="w-3 h-3" />
                )}
                {currentMode === "auto" ? "Auto" : "Draft wait"}
              </Button>
            )}

            {/* View vendor */}
            {thread.vendor_name && onViewVendor && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] px-2 text-muted-foreground hover:text-foreground"
                onClick={() => onViewVendor(thread.vendor_name!)}
                title="View vendor detail"
                aria-label="View vendor"
              >
                View vendor
              </Button>
            )}

            {/* Mark declined */}
            {(thread.status === "active" || thread.status === "needs_attention") && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleMarkDeclined}
                disabled={isMarkingDeclined}
                title="Mark this negotiation as declined"
                aria-label="Mark declined"
              >
                {isMarkingDeclined ? <Loader2 className="w-3 h-3 animate-spin" /> : "Decline"}
              </Button>
            )}

            {/* Finalize Deal */}
            {thread.status === "active" && (
              <Collapsible open={finalizeOpen} onOpenChange={setFinalizeOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-[11px] flex-shrink-0">
                    <CheckCircle2 className="w-3 h-3 mr-1 text-success" />
                    Finalize
                    <ChevronDown
                      className={cn(
                        "w-3 h-3 ml-1 transition-transform",
                        finalizeOpen && "rotate-180"
                      )}
                    />
                  </Button>
                </CollapsibleTrigger>
              </Collapsible>
            )}

            {/* Close thread */}
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0 ml-1"
                onClick={onBack}
                aria-label="Close thread"
                title="Close thread"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Finalize panel */}
        {thread.status === "active" && (
          <Collapsible open={finalizeOpen} onOpenChange={setFinalizeOpen}>
            <CollapsibleContent>
              <Card className="mt-3 p-4 bg-success/5 border-success/20">
                <p className="text-xs text-muted-foreground mb-3">
                  Record the agreed terms. This saves a Vendor Terms record and marks the thread as
                  agreed.
                </p>
                {/* Classification / proposed terms info */}
                {draftMessage?.classification && (
                  <p className="text-xs text-muted-foreground mb-3">
                    Latest classification:{" "}
                    <span className="font-medium text-foreground">
                      {draftMessage.classification}
                    </span>
                    {draftMessage.proposed_discount_pct != null && (
                      <> · Proposed discount: {draftMessage.proposed_discount_pct.toFixed(2)}%</>
                    )}
                    {draftMessage.proposed_days_early != null && (
                      <> · {draftMessage.proposed_days_early} days early</>
                    )}
                  </p>
                )}
                <div className="flex items-end gap-3">
                  <div className="space-y-1 flex-1">
                    <Label className="text-xs">Agreed APR (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={agreedApr}
                      onChange={(e) => setAgreedApr(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1 flex-1">
                    <Label className="text-xs">Days Early</Label>
                    <Input
                      type="number"
                      min="1"
                      value={agreedDays}
                      onChange={(e) => setAgreedDays(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="h-8 text-xs flex-shrink-0"
                    onClick={handleFinalize}
                    disabled={!agreedApr || !agreedDays || isFinalizing}
                  >
                    {isFinalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm"}
                  </Button>
                </div>
              </Card>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* ── Vendor context facts strip — pinned in header so the operator always
            sees the "why" without scrolling into the message list. ── */}
        {vendorContext && (
          <div data-testid="vendor-context-strip" className="mt-2 pt-2 border-t border-border/40">
            <p className="text-[11px] font-medium text-foreground leading-snug mb-1">
              {vendorContext.headline}
            </p>
            {vendorContext.reasons.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {vendorContext.reasons.map((r) => (
                  <span
                    key={r.key}
                    className="flex items-center gap-1 text-[11px] text-muted-foreground"
                  >
                    <AlertCircle className="w-2.5 h-2.5 flex-shrink-0" />
                    <span className="font-medium text-foreground/80">{r.label}</span>
                    <span className="text-muted-foreground/70">{r.detail}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Participants / metadata strip ── */}
      {(thread.participant_emails?.length ?? 0) > 0 && (
        <div className="px-4 py-1.5 border-b border-border/60 bg-background flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
          <Mail className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">
            {thread.participant_emails!.slice(0, 3).join(", ")}
            {thread.participant_emails!.length > 3 &&
              ` +${thread.participant_emails!.length - 3} more`}
          </span>
        </div>
      )}

      {/* ── Message history — dedicated scroll region; claims all remaining height ── */}
      {/*
        flex-1 min-h-0 overflow-y-auto: standard flex-scroll pattern.
        flex-1 claims all remaining vertical space after the pinned header and
        pinned bottom cluster; min-h-0 lets the container actually constrain
        height so overflow-y-auto kicks in rather than growing unbounded.
      */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-4 py-3 space-y-3">
          {sentMessages.length === 0 && !draftMessage && (
            <div className="text-center py-16 text-muted-foreground text-sm">
              {isGeneratingInitialDraft ? (
                <span
                  className="flex flex-col items-center gap-2"
                  data-testid="generating-initial-draft"
                >
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating initial draft…
                </span>
              ) : (
                "No messages yet. The AI draft will appear below once generated."
              )}
            </div>
          )}
          {sentMessages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex flex-col gap-0.5",
                msg.direction === "outbound" ? "items-end" : "items-start"
              )}
            >
              {/* Attribution row */}
              <div
                className={cn(
                  "flex items-center gap-1.5 text-[10px] text-muted-foreground px-1",
                  msg.direction === "outbound" ? "flex-row-reverse" : "flex-row"
                )}
              >
                {msg.ai_generated && (
                  <span className="flex items-center gap-0.5 text-primary">
                    <Sparkles className="w-2.5 h-2.5" />
                    AI draft
                  </span>
                )}
                {msg.send_kind === "auto" && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1 py-0 border-border/60 text-muted-foreground"
                  >
                    auto
                  </Badge>
                )}
                {msg.direction === "outbound" && !msg.ai_generated && <span>You</span>}
                <DeliveryBadge status={msg.delivery_status} />
              </div>
              <div
                className={cn(
                  "flex",
                  msg.direction === "outbound" ? "justify-end" : "justify-start"
                )}
              >
                <MessageBubble message={msg} />
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* ── Bottom cluster: draft + activity + notes — pinned, bounded height so
          the message history above always keeps the majority of the pane.
          max-h-[30vh] caps this cluster at 30% of viewport height; overflow-y-auto
          lets Activity + Notes scroll within the cluster rather than pushing the
          history up. ── */}
      <div className="flex-shrink-0 border-t border-border/60 max-h-[30vh] overflow-y-auto">
        {/* AI draft card + generate button row */}
        <div className="px-4 pt-3 pb-2">
          {draftMessage && (thread.status === "active" || thread.status === "needs_attention") && (
            <DraftCard
              message={draftMessage}
              threadId={thread.id}
              onApproveSend={approveSend}
              onDiscard={discardDraft}
              onRegenerate={regenerateDraft}
              onEdit={editDraft}
              isSending={isSending}
              isDiscarding={isDiscarding}
              isRegenerating={isRegenerating}
            />
          )}

          {/* Generate AI response — always visible so the operator can force a
              draft when auto-gen didn't fire, or request a follow-up nudge.
              Disabled while any generation is in flight. */}
          <div className="flex justify-end mt-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[11px] gap-1.5 px-2.5"
              onClick={() => generateDraft(threadId)}
              disabled={isGeneratingDraft || isGeneratingInitialDraft}
              aria-label="Generate AI response"
              data-testid="generate-ai-response-button"
            >
              {isGeneratingDraft || isGeneratingInitialDraft ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3" />
              )}
              Generate AI response
            </Button>
          </div>
        </div>

        {/* Activity trail + vendor notes — collapsed by default, expandable on demand */}
        <div className="px-4 pb-3">
          {/* ── Activity / audit trail ── */}
          <Collapsible open={activityOpen} onOpenChange={setActivityOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground py-1.5 transition-colors">
                <ChevronDown
                  className={cn("w-3 h-3 transition-transform", activityOpen && "rotate-180")}
                />
                Activity trail
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-2 pb-3 pl-1 border-l border-border/60 ml-1.5 mt-1">
                {messages.length === 0 && !thread.declined_at ? (
                  <p className="text-xs text-muted-foreground pl-3">No activity yet.</p>
                ) : (
                  <>
                    {/* Summary counts */}
                    {sentMessages.length > 0 && (
                      <div className="pl-3">
                        <ActivityEntry
                          icon={Mail}
                          label={`${sentMessages.filter((m) => m.direction === "outbound").length} sent, ${sentMessages.filter((m) => m.direction === "inbound").length} received`}
                          detail={
                            sentMessages.some((m) => m.ai_generated)
                              ? `(${sentMessages.filter((m) => m.ai_generated).length} AI-generated)`
                              : undefined
                          }
                          time={fmtTime(
                            sentMessages[sentMessages.length - 1]?.sent_at ??
                              sentMessages[sentMessages.length - 1]?.received_at ??
                              sentMessages[sentMessages.length - 1]?.created_at
                          )}
                        />
                      </div>
                    )}

                    {/* Delivery problems only (bounced / complained) */}
                    {sentMessages
                      .filter(
                        (m) => m.delivery_status === "bounced" || m.delivery_status === "complained"
                      )
                      .map((msg) => (
                        <div key={`delivery-${msg.id}`} className="pl-3">
                          <ActivityEntry
                            icon={AlertCircle}
                            label={`Delivery ${msg.delivery_status}`}
                            detail={msg.to_email ?? undefined}
                            time={fmtTime(msg.sent_at ?? msg.created_at)}
                          />
                        </div>
                      ))}

                    {/* Auto-sent messages (audit: who triggered auto-send) */}
                    {sentMessages
                      .filter((m) => m.send_kind === "auto" && m.direction === "outbound")
                      .map((msg) => (
                        <div key={`auto-${msg.id}`} className="pl-3">
                          <ActivityEntry
                            icon={Bot}
                            label="Auto-sent"
                            isAuto
                            time={fmtTime(msg.sent_at ?? msg.created_at)}
                          />
                        </div>
                      ))}

                    {/* Lifecycle: declined */}
                    {thread.declined_at && (
                      <div className="pl-3">
                        <ActivityEntry
                          icon={AlertCircle}
                          label="Marked declined"
                          time={fmtTime(thread.declined_at)}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* ── VendorNotesPanel ── */}
          {thread.vendor_name && (
            <>
              <Separator className="my-2" />
              <VendorNotesPanel vendorName={thread.vendor_name} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
